import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  agregarFuenteDesdeUrl,
  armarListaFuentes,
  fusionarFuentes,
  leerCanalesGuardados,
  quitarFuente,
  type FuenteReferencia,
  type ResultadoAgregar,
} from "./canalesReferencia";

/**
 * Los canales de referencia agregados por el usuario, guardados POR MARCA en
 * localStorage.
 *
 * DÓNDE VIVE ESTO Y POR QUÉ. No hay tabla ni endpoint todavía: el
 * procesamiento de los canales es trabajo posterior, y esta pantalla es la UI
 * que lo va a alimentar. Las dos opciones honestas eran memoria (se pierde al
 * cambiar de pantalla) o navegador; se eligió el navegador para que la lista
 * sobreviva al reload —si no, cargar diez referentes y perderlos al navegar
 * sería peor que no dejarlos cargar— pero la pantalla lo DICE con todas las
 * letras: no está en la cuenta y Atlas todavía no lo lee. Guardarlo en
 * silencio como si estuviera en la cuenta es justo lo que no se puede hacer.
 *
 * Mismo patrón (y mismos motivos) que `datos/configMapa.ts`: clave con el id de
 * la marca adentro, lectura blindada, escritura tolerante al storage
 * deshabilitado. Cuando exista el backend, este archivo es el único a cambiar.
 * PERO con una diferencia que no se puede copiar: acá el dato es una LISTA
 * ACUMULATIVA, no un objeto de config que se reemplaza entero, así que lo
 * agregado mientras no había marca se FUSIONA con lo guardado en vez de
 * pisarlo — ver el efecto de abajo.
 *
 * Ver ADR-2026-08-17-estudio-marketing-datos-canales-referencia y
 * ADR-2026-08-17-estudio-marketing-fixes-revision.
 */

const PREFIJO = "atlas:estudio-marketing:canales-referencia:";

export const claveCanalesReferencia = (brandId: string) => `${PREFIJO}${brandId}`;

/** El `try` cubre el storage deshabilitado (Safari privado, iframes con cookies
 *  bloqueadas), donde `getItem` no devuelve null sino que TIRA. */
function leerDelStorage(brandId: string | null): FuenteReferencia[] {
  if (!brandId) return [];
  try {
    return leerCanalesGuardados(window.localStorage.getItem(claveCanalesReferencia(brandId)));
  } catch {
    return [];
  }
}

/** Devuelve si pudo escribir. La pantalla usa ese `false` para avisar que lo
 *  cargado no va a sobrevivir al reload — es la misma regla de honestidad que
 *  el resto del bloque: nada se acepta en silencio. */
function escribirEnStorage(brandId: string, fuentes: FuenteReferencia[]): boolean {
  try {
    window.localStorage.setItem(claveCanalesReferencia(brandId), JSON.stringify(fuentes));
    return true;
  } catch {
    return false;
  }
}

/**
 * Por qué no se está guardando. Los dos casos NO se le pueden contar igual al
 * usuario: "todavía no hay marca" dura dos segundos y termina bien (lo cargado
 * se guarda apenas resuelve el perfil), mientras que "el navegador no deja"
 * significa que lo cargado se pierde al recargar. Decirle "no se está
 * guardando en ningún lado" al primero es asustarlo con algo que no va a pasar.
 */
export type MotivoSinPersistir = "sin-marca" | "storage";

export interface CanalesReferencia {
  /** La lista completa: las oficiales de Meta + las agregadas. */
  fuentes: FuenteReferencia[];
  /** Sólo las agregadas (lo que se puede quitar y lo que se persiste). */
  agregadas: FuenteReferencia[];
  agregar: (url: string, nombre?: string) => ResultadoAgregar;
  quitar: (id: string) => void;
  /**
   * `false` = lo agregado NO se está guardando ni siquiera en este navegador:
   * o todavía no hay marca activa, o el storage está bloqueado/lleno. La
   * pantalla lo tiene que decir.
   */
  persiste: boolean;
  /** Cuál de los dos motivos, cuando `persiste` es `false`. */
  motivoSinPersistir: MotivoSinPersistir | null;
}

export function useCanalesReferencia(brandId: string | null): CanalesReferencia {
  const [agregadas, setAgregadas] = useState<FuenteReferencia[]>(() => leerDelStorage(brandId));
  const [storageOk, setStorageOk] = useState(true);
  /** Lo que se agregó mientras `brandId` todavía era `null`. */
  const pendienteSinMarca = useRef<FuenteReferencia[] | null>(null);

  useEffect(() => {
    const pendiente = pendienteSinMarca.current;
    if (brandId && pendiente) {
      // Llegó la marca y hay fuentes esperando. SE FUSIONA con lo que la marca
      // ya tenía guardado, nunca se pisa: lo pendiente es un DELTA armado sobre
      // una lista vacía (sin marca no hay clave que leer), así que escribirlo
      // solo borraría todos los referentes que el usuario había cargado antes.
      // Acá NO vale el criterio de `useConfigMapa` ("la última elección le gana
      // a lo guardado"): allá lo pendiente es el objeto de config completo —dos
      // booleanos que el usuario acaba de ver y elegir— y reemplazar es
      // correcto; acá es una lista acumulativa y reemplazar es borrar.
      // El estado SÍ se pisa, con el resultado de la fusión: si no, la pantalla
      // seguiría mostrando sólo lo pendiente mientras el storage ya tiene las
      // dos cosas (ADR-2026-08-17-estudio-marketing-fixes-revision).
      pendienteSinMarca.current = null;
      const fusionadas = fusionarFuentes(leerDelStorage(brandId), pendiente);
      setAgregadas(fusionadas);
      setStorageOk(escribirEnStorage(brandId, fusionadas));
      return;
    }
    // Cambió la marca activa ⇒ la lista es otra. La base de conocimiento es de
    // UNA marca: arrastrar los referentes de la anterior sería mostrarle a esta
    // marca fuentes que nadie cargó para ella.
    setAgregadas(leerDelStorage(brandId));
    setStorageOk(true);
  }, [brandId]);

  const agregar = useCallback(
    (url: string, nombre?: string): ResultadoAgregar => {
      const resultado = agregarFuenteDesdeUrl(agregadas, url, nombre);
      if (!resultado.ok) return resultado;
      setAgregadas(resultado.agregadas);
      if (!brandId) {
        // Sin clave con la que persistir. Queda anotado para escribirlo apenas
        // haya marca; si nunca la hay, vale lo que vive la pestaña.
        pendienteSinMarca.current = resultado.agregadas;
        return resultado;
      }
      setStorageOk(escribirEnStorage(brandId, resultado.agregadas));
      return resultado;
    },
    [agregadas, brandId],
  );

  const quitar = useCallback(
    (id: string) => {
      const siguiente = quitarFuente(agregadas, id);
      setAgregadas(siguiente);
      if (!brandId) {
        pendienteSinMarca.current = siguiente;
        return;
      }
      setStorageOk(escribirEnStorage(brandId, siguiente));
    },
    [agregadas, brandId],
  );

  const fuentes = useMemo(() => armarListaFuentes(agregadas), [agregadas]);

  return {
    fuentes,
    agregadas,
    agregar,
    quitar,
    persiste: Boolean(brandId) && storageOk,
    motivoSinPersistir: !brandId ? "sin-marca" : storageOk ? null : "storage",
  };
}
